export const BRAND_KEYS = ['lead', 'appiq', 'jobiq', 'stellencockpit', 'recruitingiq', 'jobaffairs'] as const;

export type BrandKey = (typeof BRAND_KEYS)[number];

export function isBrandKey(key: string | null | undefined): key is BrandKey {
  return BRAND_KEYS.includes(key as BrandKey);
}

export interface BrandFont {
  /** CSS font-family value incl. fallbacks */
  family: string;
  /** optional font file, declared as a variable-weight @font-face for the first family name */
  file?: string;
}

export type LegalLinkLanguage = 'de' | 'en';

/** one URL for every language, or one per language with `en` as the fallback */
export type BrandLegalLink = string | ({ en: string } & Partial<Record<LegalLinkLanguage, string>>);

export interface BrandLegalLinks {
  imprint?: BrandLegalLink;
  privacy?: BrandLegalLink;
  terms?: BrandLegalLink;
}

/** the brand identity colours; neutrals and status colours stay global. Mapped to CSS vars in applyBrandTheme */
export interface BrandTheme {
  primary: string;
  primaryHover: string;
  primarySoft: string;
  accent: string;
  accentOnDark: string;
  /** background of an accent button on hover, and its text colour */
  accentHover: string;
  accentText: string;
  sidebarBg: string;
  panelBg: string;
  sidebarLink: string;
  pageBg: string;
  text: string;
  border: string;
}

export interface BrandLogos {
  main: string;
  text: string;
  panel: string;
  /** the panel logo is a dark artwork that has to be turned white on the dark panel */
  panelInvert: boolean;
}

export interface BrandWordmark {
  light: string;
  bold: string;
}

export interface Brand {
  key: BrandKey;
  /** drives the Recruiting IQ specific copy and layout */
  appType: 'LEAD' | 'HRIQ';
  name: string;
  shortName: string;
  /** the few letters shown in the login panel circle */
  emblem: string;
  /** the product the customer buys, used where a string names the product rather than the app */
  productName: string;
  /** two-letter initials of the product, shown in the package badge */
  productBadge: string;
  /** shown after the year in the login and page footers */
  legalName: string;
  /** "by ..." line in the login brand panel */
  byline: string | null;
  logos: BrandLogos;
  /** a text wordmark replaces the logo images in the sidebar and login screens when set */
  wordmark: BrandWordmark | null;
  /** locale key of the login panel headline */
  panelHeadlineKey: string;
  /** overrides of the default theme, applied on :root at startup */
  theme: Partial<BrandTheme>;
  font?: BrandFont;
  /** public path holding favicon, apple-touch-icon and manifest; unset keeps the default icons */
  assetDir?: string;
  legalLinks: BrandLegalLinks;
}
